import { Box } from "@mui/material";
import { useExcludedPeriods } from "components/manualReview/contexts/ExcludedPeriodsContext";
import { addEventMetadata } from "components/manualReview/utils/formatters";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { LabellerPeriodWithExpandedEvents } from "../hooks/useTimelinesByLabeller";
import { EventTimelineTrack } from "./EventTimelineTrack";

interface LabellerTimelinePeriodProps {
  period: LabellerPeriodWithExpandedEvents;
  visibleStart: Dayjs;
  visibleEnd: Dayjs;
  visibleDuration: number;
  currentTime?: number;
  showPlayhead?: boolean;
}

export const LabellerTimelinePeriod = ({
  period,
  visibleStart,
  visibleEnd,
  visibleDuration,
  currentTime = 0,
  showPlayhead = false,
}: LabellerTimelinePeriodProps) => {
  const { default_areas: defaultAreas } = useScrapEyeMetadata();
  const { areAllExcluded } = useExcludedPeriods();
  const excluded = areAllExcluded(period.periodIds);

  const actualVisibleStart = period.start.isBefore(visibleStart)
    ? visibleStart
    : period.start;
  const actualVisibleEnd = period.end.isAfter(visibleEnd)
    ? visibleEnd
    : period.end;

  const visibleStartOffset = actualVisibleStart.diff(
    visibleStart,
    "second",
    true
  );
  const visibleEndOffset = actualVisibleEnd.diff(visibleStart, "second", true);

  const leftPercent = (visibleStartOffset / visibleDuration) * 100;
  const widthPercent =
    ((visibleEndOffset - visibleStartOffset) / visibleDuration) * 100;

  const sortedEvents = useMemo(() => {
    return period.events.sort((a, b) => a.start.diff(b.start, "second", true));
  }, [period.events]);

  const eventsWithMetadata = useMemo(
    () => addEventMetadata(sortedEvents, defaultAreas),
    [sortedEvents, defaultAreas]
  );

  // A labeller period can span far beyond the visible window; its events are
  // positioned against that window, so events outside it would land off to the
  // side. Render only those that overlap the visible range.
  const visibleEvents = useMemo(
    () =>
      eventsWithMetadata.filter(
        (event) =>
          event.end.isAfter(visibleStart) && event.start.isBefore(visibleEnd)
      ),
    [eventsWithMetadata, visibleStart, visibleEnd]
  );

  if (period.end.isBefore(visibleStart) || period.start.isAfter(visibleEnd)) {
    return null;
  }

  return (
    <Box
      key={period.periodId}
      sx={{
        position: "relative",
        width: "100%",
        opacity: excluded ? 0.35 : 1,
        transition: "opacity 0.15s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          height: "100%",
          backgroundColor: "action.hover",
          borderRadius: 0.5,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <EventTimelineTrack
          events={visibleEvents}
          videoStart={visibleStart}
          videoDuration={visibleDuration}
          currentTime={currentTime}
          eventHeight={8}
          overbarHeight={11}
          showPlayhead={showPlayhead}
        />
      </Box>
    </Box>
  );
};
